import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  Chip,
  Paper,
  IconButton,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { ImportTrabajadorService } from '../services/importService';
import { ConceptoService } from '../../conceptos/services/conceptoService';
import type { ConceptoPlanilla } from '../../conceptos/types/concepto';
import type { ImportResult, ExcelPreviewData } from '../types/import.types';

interface Props {
  open: boolean;
  onClose: () => void;
  onImportComplete: () => void;
}

type ImportStep = 'upload' | 'preview' | 'importing' | 'result';

const ImportTrabajadoresDialog: React.FC<Props> = ({ open, onClose, onImportComplete }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [step, setStep] = useState<ImportStep>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ExcelPreviewData | null>(null);
  const [previewTab, setPreviewTab] = useState(0);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const conceptosRef = useRef<ConceptoPlanilla[]>([]);

  useEffect(() => {
    if (open && conceptosRef.current.length === 0) {
      ConceptoService.getAll().then((data) => {
        conceptosRef.current = data;
      }).catch(() => {});
    }
  }, [open]);

  const resetState = () => {
    setStep('upload');
    setFile(null);
    setPreview(null);
    setPreviewTab(0);
    setImporting(false);
    setResult(null);
    setError(null);
    setDragOver(false);
  };

  const handleClose = () => {
    if (result && result.totalCreados > 0) {
      onImportComplete();
    }
    resetState();
    onClose();
  };

  const handleFileSelect = useCallback(async (selectedFile: File) => {
    if (!selectedFile.name.endsWith('.xlsx')) {
      setError('Solo se permiten archivos .xlsx');
      return;
    }

    setFile(selectedFile);
    setError(null);

    try {
      const previewData = await ImportTrabajadorService.parseExcelPreview(selectedFile);
      setPreview(previewData);
      setStep('preview');
    } catch {
      setError('Error al leer el archivo Excel');
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) handleFileSelect(droppedFile);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) handleFileSelect(selectedFile);
  }, [handleFileSelect]);

  const handleImport = async () => {
    if (!file) return;

    setStep('importing');
    setImporting(true);
    setError(null);

    try {
      const importResult = await ImportTrabajadorService.uploadExcel(file);
      setResult(importResult);
      setStep('result');
    } catch (err: unknown) {
      const errorObj = err as Record<string, unknown>;
      setError(
        (errorObj?.userMessage as string) ||
        (errorObj?.message as string) ||
        'Error al importar el archivo'
      );
      setStep('preview');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await ImportTrabajadorService.downloadTemplate();
    } catch {
      setError('Error al descargar la plantilla');
    }
  };

  const getPreviewColumns = (data: Record<string, unknown>[]): GridColDef[] => {
    if (data.length === 0) return [];
    return Object.keys(data[0]).map((key) => ({
      field: key,
      headerName: key === 'NombreConcepto' ? 'Nombre Concepto' : key,
      width: 150,
      flex: (key === 'NombreCompleto' || key === 'NombreConcepto') ? 1 : undefined,
      minWidth: key === 'NombreConcepto' ? 200 : undefined,
    }));
  };

  const getPreviewData = () => {
    if (!preview) return [];
    const sheets = [preview.trabajadores, preview.dependientes, preview.direcciones, preview.conceptos];
    const data = sheets[previewTab] || [];
    // Enrich conceptos tab with concept name
    if (previewTab === 3 && data.length > 0 && conceptosRef.current.length > 0) {
      return data.map((row) => {
        const codigo = String(row.CodigoConcepto ?? '').trim();
        const concepto = conceptosRef.current.find(
          (c) => c.codigo === codigo || c.codigo === codigo.padStart(4, '0')
        );
        return { ...row, NombreConcepto: concepto?.descripcion ?? '' };
      });
    }
    return data;
  };

  const getSheetCounts = () => {
    if (!preview) return { trabajadores: 0, dependientes: 0, direcciones: 0, conceptos: 0 };
    return {
      trabajadores: preview.trabajadores.length,
      dependientes: preview.dependientes.length,
      direcciones: preview.direcciones.length,
      conceptos: preview.conceptos.length,
    };
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Importar Trabajadores desde Excel</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* STEP: UPLOAD */}
        {step === 'upload' && (
          <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadTemplate}
              >
                Descargar Plantilla Excel
              </Button>
            </Box>

            <Paper
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              sx={{
                p: 6,
                textAlign: 'center',
                border: '2px dashed',
                borderColor: dragOver ? 'primary.main' : 'divider',
                backgroundColor: dragOver ? 'action.hover' : 'background.default',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': { borderColor: 'primary.light', backgroundColor: 'action.hover' },
              }}
              onClick={() => document.getElementById('excel-file-input')?.click()}
            >
              <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Arrastra el archivo Excel aqui
              </Typography>
              <Typography variant="body2" color="text.secondary">
                o haz clic para seleccionar un archivo .xlsx
              </Typography>
              <input
                id="excel-file-input"
                type="file"
                accept=".xlsx"
                onChange={handleInputChange}
                style={{ display: 'none' }}
              />
            </Paper>
          </Box>
        )}

        {/* STEP: PREVIEW */}
        {step === 'preview' && preview && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <FileIcon color="primary" />
              <Typography variant="body1" fontWeight="bold">
                {file?.name}
              </Typography>
              <Button size="small" onClick={resetState}>Cambiar archivo</Button>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              {(() => {
                const counts = getSheetCounts();
                return (
                  <>
                    <Chip label={`${counts.trabajadores} Trabajadores`} color="primary" variant="outlined" />
                    <Chip label={`${counts.dependientes} Dependientes`} color="success" variant="outlined" />
                    <Chip label={`${counts.direcciones} Direcciones`} color="warning" variant="outlined" />
                    <Chip label={`${counts.conceptos} Conceptos`} color="error" variant="outlined" />
                  </>
                );
              })()}
            </Box>

            <Tabs value={previewTab} onChange={(_, v) => setPreviewTab(v)} sx={{ mb: 2 }}>
              <Tab label="Trabajadores" />
              <Tab label="Dependientes" />
              <Tab label="Direcciones" />
              <Tab label="Conceptos" />
            </Tabs>

            <Paper sx={{ height: 350 }}>
              <DataGrid
                rows={getPreviewData().map((row, idx) => ({ ...row, _id: idx }))}
                columns={getPreviewColumns(getPreviewData())}
                getRowId={(row) => row._id}
                pageSizeOptions={[10, 25]}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                density="compact"
                disableRowSelectionOnClick
                sx={{ border: 'none' }}
              />
            </Paper>
          </Box>
        )}

        {/* STEP: IMPORTING */}
        {step === 'importing' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" gutterBottom>
              Importando trabajadores...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Por favor espere, esto puede tomar unos momentos
            </Typography>
            <LinearProgress />
          </Box>
        )}

        {/* STEP: RESULT */}
        {step === 'result' && result && (
          <Box>
            <Alert
              severity={result.totalErrores === 0 ? 'success' : 'warning'}
              sx={{ mb: 3 }}
              icon={result.totalErrores === 0 ? <SuccessIcon /> : <ErrorIcon />}
            >
              {result.totalErrores === 0
                ? 'Importacion completada exitosamente'
                : 'Importacion completada con algunos errores'}
            </Alert>

            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <Paper sx={{ p: 2, flex: 1, minWidth: 150, textAlign: 'center' }}>
                <Typography variant="h4" color="primary">{result.totalCreados}</Typography>
                <Typography variant="body2" color="text.secondary">Trabajadores creados</Typography>
              </Paper>
              <Paper sx={{ p: 2, flex: 1, minWidth: 150, textAlign: 'center' }}>
                <Typography variant="h4" color="success.main">{result.dependientesCreados}</Typography>
                <Typography variant="body2" color="text.secondary">Dependientes</Typography>
              </Paper>
              <Paper sx={{ p: 2, flex: 1, minWidth: 150, textAlign: 'center' }}>
                <Typography variant="h4" color="warning.main">{result.direccionesCreadas}</Typography>
                <Typography variant="body2" color="text.secondary">Direcciones</Typography>
              </Paper>
              <Paper sx={{ p: 2, flex: 1, minWidth: 150, textAlign: 'center' }}>
                <Typography variant="h4" color="info.main">{result.conceptosCreados}</Typography>
                <Typography variant="body2" color="text.secondary">Conceptos</Typography>
              </Paper>
              {result.totalErrores > 0 && (
                <Paper sx={{ p: 2, flex: 1, minWidth: 150, textAlign: 'center' }}>
                  <Typography variant="h4" color="error">{result.totalErrores}</Typography>
                  <Typography variant="body2" color="text.secondary">Errores</Typography>
                </Paper>
              )}
            </Box>

            {result.errores.length > 0 && (
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                  Detalle de errores
                </Typography>
                <Paper sx={{ height: 250 }}>
                  <DataGrid
                    rows={result.errores.map((e, idx) => ({ ...e, id: idx }))}
                    columns={[
                      { field: 'hoja', headerName: 'Hoja', width: 130 },
                      { field: 'fila', headerName: 'Fila', width: 80 },
                      { field: 'campo', headerName: 'Campo', width: 180 },
                      { field: 'mensaje', headerName: 'Mensaje', flex: 1, minWidth: 250 },
                    ]}
                    pageSizeOptions={[10, 25]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    density="compact"
                    disableRowSelectionOnClick
                    sx={{ border: 'none' }}
                  />
                </Paper>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          {step === 'result' ? 'Cerrar' : 'Cancelar'}
        </Button>
        {step === 'preview' && (
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={importing}
            startIcon={<UploadIcon />}
          >
            Importar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportTrabajadoresDialog;
